<template>
  <div class="product-option-modal">
    <TextInput
      :id="inputs.label.id"
      :label="inputs.label.label"
      v-model="inputs.label.binding"
      :error="inputs.label.error"
      :rules="inputs.label.rules"
      :placeholder="inputs.label.placeholder"
      @validate="onValidate('label', $event)"
    />

    <TextInput
      :id="inputs.caption.id"
      :label="inputs.caption.label"
      v-model="inputs.caption.binding"
      :error="inputs.caption.error"
      :rules="inputs.caption.rules"
      :placeholder="inputs.caption.placeholder"
      @validate="onValidate('caption', $event)"
    />

    <SelectInput
      :id="inputs.type.id"
      :label="inputs.type.label"
      v-model="inputs.type.binding"
      :error="inputs.type.error"
      :rules="inputs.type.rules"
      :options="[
        { value: 'number', label: 'Getal (slider / invoer)' },
        { value: 'single-choice', label: 'Single Choice' },
        { value: 'multi-choice', label: 'Multi Choice' },
        { value: 'toggle', label: 'Aan/uit schakelaar' },
        { value: 'text', label: 'Tekstveld' },
        { value: 'notice', label: 'Informatiemelding' },
        { value: 'image', label: 'Afbeelding' },
        { value: 'photo', label: 'Situatiefoto\'s (upload)' },
      ]"
      @validate="onValidate('type', $event)"
    />

    <div v-if="!isContentType" class="form-toggles">
      <ToggleInput id="option_required" label="Verplicht veld" v-model="optionForm.required" />

      <ToggleInput
        id="option_show_on_quote"
        label="Toon als aparte regel op offerte"
        v-model="optionForm.showOnQuote"
      />

      <ToggleInput
        id="option_show_on_packing_slip"
        label="Toon op pakbon"
        v-model="optionForm.showOnPackingSlip"
      />

      <ToggleInput
        id="option_hide_on_quote"
        label="Toon niet op offerte"
        v-model="optionForm.hideOnQuote"
      />

      <ToggleInput
        id="option_internal_only"
        label="Alleen intern (inmeten/bestelling)"
        v-model="optionForm.internalOnly"
        :disabled="optionForm.externalOnly"
      />

      <ToggleInput
        id="option_external_only"
        label="Alleen extern (klant; verbergen bij inmeten)"
        v-model="optionForm.externalOnly"
        :disabled="optionForm.internalOnly"
      />
    </div>

    <div v-if="isContentType" class="form-toggles">
      <ToggleInput
        v-if="inputs.type.binding === 'photo'"
        id="option_required"
        label="Verplicht veld"
        v-model="optionForm.required"
      />
      <ToggleInput
        id="option_internal_only_content"
        label="Alleen intern (inmeten/bestelling)"
        v-model="optionForm.internalOnly"
        :disabled="optionForm.externalOnly"
      />
      <ToggleInput
        id="option_external_only_content"
        label="Alleen extern (klant; verbergen bij inmeten)"
        v-model="optionForm.externalOnly"
        :disabled="optionForm.internalOnly"
      />
    </div>

    <!-- Pagina Groep -->
    <SelectInput
      id="option_page_group"
      label="Pagina Groep"
      v-model="optionForm.pageGroupId"
      :options="pageGroupOptions"
      placeholder="Geen groep (eigen pagina)"
      :allow-create="true"
      create-placeholder="Nieuwe groep naam..."
      @create-option="handleCreateGroup"
    />
    <p class="field-description">
      Opties met dezelfde groep worden samen op één pagina getoond in de configurator.
    </p>

    <!-- Breedte (naast elkaar binnen een groep) -->
    <SelectInput
      id="option_width"
      label="Breedte"
      v-model="optionForm.width"
      :options="[
        { value: 'full', label: 'Volledig (100%)' },
        { value: 'half', label: 'Helft (1/2)' },
        { value: 'third', label: 'Eén derde (1/3)' },
        { value: 'quarter', label: 'Eén vierde (1/4)' },
      ]"
    />
    <p class="field-description">
      Bepaalt hoe breed de optie is. Kleinere breedtes worden naast elkaar getoond wanneer opties
      in dezelfde groep staan.
    </p>

    <!-- Notice configuratie -->
    <div v-if="inputs.type.binding === 'notice'" class="config-section">
      <h4>Melding Configuratie</h4>
      <WysiwygEditor
        id="notice_content"
        label="Inhoud"
        v-model="optionForm.noticeConfig.content"
      />
      <SelectInput
        id="notice_variant"
        label="Stijl"
        v-model="optionForm.noticeConfig.variant"
        :options="[
          { value: 'info', label: 'Info (blauw)' },
          { value: 'warning', label: 'Waarschuwing (geel)' },
          { value: 'success', label: 'Succes (groen)' },
          { value: 'error', label: 'Fout (rood)' },
          { value: 'brand', label: 'Merk (design kleuren)' },
        ]"
      />
    </div>

    <!-- Image configuratie -->
    <div v-if="inputs.type.binding === 'image'" class="config-section">
      <h4>Afbeelding Configuratie</h4>
      <div class="file-input-wrapper">
        <FileInput
          id="image_src"
          label="Afbeelding"
          v-model="optionForm.imageConfig.src"
          :upload-config="{
            type: 'option_image',
            context: 'product_option',
          }"
          accept="image/*"
          @click.stop
        />
      </div>
      <TextInput
        id="image_alt"
        label="Alt tekst"
        v-model="optionForm.imageConfig.alt"
        placeholder="Beschrijving van de afbeelding"
      />
      <SelectInput
        id="image_alignment"
        label="Uitlijning"
        v-model="optionForm.imageConfig.alignment"
        :options="[
          { value: 'left', label: 'Links' },
          { value: 'center', label: 'Midden' },
          { value: 'right', label: 'Rechts' },
        ]"
      />
    </div>

    <!-- Photo configuratie -->
    <div v-if="inputs.type.binding === 'photo'" class="config-section">
      <h4>Situatiefoto's configuratie</h4>
      <p class="field-description">
        De klant/gebruiker kan hier foto's uploaden als situatiefoto's. Deze worden automatisch
        gekoppeld aan het project.
      </p>
      <div class="form-row">
        <NumberInput
          id="photo_min"
          label="Minimum aantal foto's"
          v-model="optionForm.photoConfig.min"
          :min="0"
          :max="20"
        />
        <NumberInput
          id="photo_max"
          label="Maximum aantal foto's"
          v-model="optionForm.photoConfig.max"
          :min="1"
          :max="20"
        />
      </div>
      <TextInput
        id="photo_placeholder"
        label="Instructie voor de klant"
        v-model="optionForm.photoConfig.instruction"
        placeholder="bijv. Maak een foto van de huidige situatie..."
      />
    </div>

    <!-- Text configuratie -->
    <div v-if="inputs.type.binding === 'text'" class="config-section">
      <h4>Tekstveld Configuratie</h4>
      <SelectInput
        id="text_variant"
        label="Type"
        v-model="optionForm.textConfig.variant"
        :options="[
          { value: 'short-answer', label: 'Kort antwoord (1 regel)' },
          { value: 'long-answer', label: 'Lang antwoord (meerdere regels)' },
        ]"
      />
      <TextInput
        id="text_placeholder"
        label="Placeholder"
        v-model="optionForm.textConfig.placeholder"
        placeholder="bijv. Voer hier je tekst in..."
      />
    </div>

    <!-- Number configuratie -->
    <div v-if="inputs.type.binding === 'number'" class="config-section">
      <h4>Number Configuratie</h4>
      <SelectInput
        id="number_display_style"
        label="Weergave"
        v-model="optionForm.numberConfig.displayStyle"
        :options="[
          { value: 'slider', label: 'Slider (met invoerveld)' },
          { value: 'input', label: 'Alleen invoerveld' },
        ]"
      />
      <div class="form-row">
        <NumberInput id="number_min" label="Minimum" v-model="optionForm.numberConfig.min" />
        <NumberInput id="number_max" label="Maximum" v-model="optionForm.numberConfig.max" />
      </div>
      <div class="form-row">
        <NumberInput id="number_step" label="Stap" v-model="optionForm.numberConfig.step" />
        <NumberInput
          id="number_default"
          label="Default waarde"
          v-model="optionForm.numberConfig.defaultValue"
        />
      </div>

      <!-- Dynamische grenzen (min/max afhankelijk van een andere optie) -->
      <div class="dynamic-bounds">
        <div v-for="kind in dynamicBoundKinds" :key="kind.key" class="dynamic-bound">
          <ToggleInput
            :id="`number_${kind.key}_enabled`"
            :label="`Dynamisch ${kind.label} (afhankelijk van andere optie)`"
            :modelValue="!!optionForm.numberConfig[kind.key]"
            @update:modelValue="(v) => toggleDynamicBound(kind.key, v)"
          />

          <div v-if="optionForm.numberConfig[kind.key]" class="dynamic-bound__config">
            <SelectInput
              :id="`number_${kind.key}_option`"
              label="Gekoppeld aan optie"
              v-model="optionForm.numberConfig[kind.key].optionId"
              :options="conditionableOptions"
              placeholder="Selecteer optie"
              @update:modelValue="() => onDynamicBoundOptionChange(kind.key)"
            />

            <template v-if="optionForm.numberConfig[kind.key].optionId">
              <div
                v-for="(row, rIdx) in optionForm.numberConfig[kind.key].map"
                :key="rIdx"
                class="dynamic-bound__row"
              >
                <SelectInput
                  v-if="dynamicBoundRefType(kind.key) !== 'number'"
                  :id="`number_${kind.key}_value_${rIdx}`"
                  label="Als waarde is"
                  v-model="row.value"
                  :options="getChoiceOptions(optionForm.numberConfig[kind.key].optionId)"
                />
                <NumberInput
                  v-else
                  :id="`number_${kind.key}_value_${rIdx}`"
                  label="Als waarde is"
                  v-model="row.value"
                />
                <NumberInput
                  :id="`number_${kind.key}_bound_${rIdx}`"
                  :label="kind.label === 'minimum' ? 'dan min.' : 'dan max.'"
                  v-model="row.bound"
                />
                <Btn
                  variant="plain"
                  icon="trash"
                  type="button"
                  @click="removeDynamicBoundRow(kind.key, rIdx)"
                />
              </div>

              <div class="dynamic-bound__actions">
                <Btn variant="dashed" size="sm" type="button" @click="addDynamicBoundRow(kind.key)">
                  Regel toevoegen
                </Btn>
                <Btn
                  v-if="dynamicBoundRefType(kind.key) !== 'number'"
                  variant="tertiary"
                  size="sm"
                  type="button"
                  @click="fillDynamicBoundChoices(kind.key)"
                >
                  Alle keuzes invullen
                </Btn>
              </div>

              <NumberInput
                :id="`number_${kind.key}_fallback`"
                label="Fallback (geen match)"
                v-model="optionForm.numberConfig[kind.key].fallback"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- Pricing Configuration -->
      <div class="pricing-section">
        <h5>Prijsstelling</h5>
        <SelectInput
          id="pricing_type"
          label="Pricing Type"
          v-model="optionForm.numberConfig.pricingType"
          :options="[
            { value: 'per_unit', label: 'Per eenheid (€2 per cm)' },
            { value: 'formula', label: 'Formule (aangepaste berekening)' },
            { value: 'matrix', label: 'Matrix (B×H berekening)' },
            { value: 'fixed', label: 'Vaste prijs (ongeacht waarde)' },
          ]"
        />

        <!-- Per Unit Pricing -->
        <div v-if="optionForm.numberConfig.pricingType === 'per_unit'" class="pricing-config">
          <div class="form-row">
            <CurrencyInput
              id="number_base_price"
              label="Basisprijs"
              v-model="optionForm.numberConfig.basePrice"
              :allow-negative="true"
            />
            <CurrencyInput
              id="number_price_per_unit"
              label="Prijs per eenheid"
              v-model="optionForm.numberConfig.pricePerUnit"
              :allow-negative="true"
            />
          </div>
        </div>

        <!-- Fixed Pricing -->
        <div v-if="optionForm.numberConfig.pricingType === 'fixed'" class="pricing-config">
          <CurrencyInput
            id="number_fixed_price"
            label="Vaste prijs"
            v-model="optionForm.numberConfig.fixedPrice"
            :allow-negative="true"
          />
        </div>

        <!-- Formula Pricing -->
        <div v-if="optionForm.numberConfig.pricingType === 'formula'" class="pricing-config">
          <FormulaEditor
            id="pricing_formula"
            label="Formule"
            v-model="optionForm.numberConfig.formula"
            :custom-fields="formulaFields"
            :show-conditional-operators="false"
            compact
          />
          <div class="formula-help">
            <small>
              <strong>Beschikbare variabelen:</strong>
              Klik op velden om ze als tag toe te voegen. Formules worden opgeslagen als
              <code>{'{'}option_id{'}'}</code>.
              <br />
              <strong>Voorbeelden:</strong>
              <code>{value} * 2.5</code>
              (€2.50 per eenheid),
              <code>{value} * {value} * 0.01</code>
              (vierkant × €0.01)
            </small>
          </div>
        </div>

        <!-- Matrix Pricing -->
        <div v-if="optionForm.numberConfig.pricingType === 'matrix'" class="pricing-config">
          <div class="matrix-info">
            <p>
              <strong>Matrix Pricing:</strong>
              Waarde × Andere waarde × Prijs per eenheid
            </p>
          </div>

          <div v-if="!matrixFieldOptions.length" class="no-matrix-options">
            <MessageElement type="warning">
              <strong>Geen andere number opties beschikbaar</strong>
              <br />
              Voeg eerst andere number-type opties toe om matrix pricing te kunnen gebruiken.
            </MessageElement>
          </div>

          <div v-else>
            <SelectInput
              id="matrix_other_field"
              label="Koppel met veld"
              v-model="optionForm.numberConfig.matrixConfig.otherField"
              :options="matrixFieldOptions"
              placeholder="Selecteer het andere veld"
            />
            <CurrencyInput
              id="matrix_price_per_unit"
              label="Prijs per eenheid (€)"
              v-model="optionForm.numberConfig.matrixConfig.pricePerUnit"
              :allow-negative="true"
            />
            <NumberInput
              id="matrix_min_price"
              label="Minimum prijs (€)"
              v-model="optionForm.numberConfig.matrixConfig.minimumPrice"
            />
          </div>
        </div>

        <!-- Pricing Preview -->
        <div v-if="optionForm.numberConfig.pricingType !== 'per_unit'" class="pricing-preview">
          <h6>Prijs Preview</h6>
          <div class="preview-inputs">
            <NumberInput
              id="preview_value"
              label="Test waarde"
              v-model="previewValue"
              :min="optionForm.numberConfig.min"
              :max="optionForm.numberConfig.max"
            />
            <NumberInput
              v-if="optionForm.numberConfig.pricingType === 'matrix'"
              id="preview_other_value"
              label="Andere waarde"
              v-model="previewOtherValue"
            />
          </div>
          <div class="preview-result">
            <strong>Berekende prijs: €{{ calculatedPreviewPrice }}</strong>
            <div v-if="previewCalculation" class="calculation-breakdown">
              <small>{{ previewCalculation }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toggle configuratie -->
    <div v-if="inputs.type.binding === 'toggle'" class="config-section">
      <h4>Aan/uit Configuratie</h4>
      <p class="field-description">
        De klant ziet een schakelaar. Wanneer deze aan staat wordt de ingestelde prijs toegevoegd.
      </p>
      <div class="form-row">
        <SelectInput
          id="toggle_pricing_type"
          label="Prijsberekening"
          v-model="optionForm.toggleConfig.pricingType"
          :options="optionPriceTypeOptions"
        />
        <CurrencyInput
          v-if="optionForm.toggleConfig.pricingType !== 'formula'"
          id="toggle_price"
          label="Prijs wanneer aan"
          v-model="optionForm.toggleConfig.price"
          :allow-negative="true"
        />
        <FormulaEditor
          v-else
          id="toggle_price_formula"
          label="Prijsformule wanneer aan"
          v-model="optionForm.toggleConfig.priceFormula"
          :custom-fields="formulaFields"
          :show-conditional-operators="false"
          compact
        />
        <TextInput
          id="toggle_sku_suffix"
          label="SKU Suffix (wanneer aan)"
          v-model="optionForm.toggleConfig.skuSuffix"
          placeholder="bijv. MOTOR"
        />
      </div>
      <div class="form-row">
        <TextInput
          id="toggle_on_label"
          label="Label 'aan'"
          v-model="optionForm.toggleConfig.onLabel"
          placeholder="Ja"
        />
        <TextInput
          id="toggle_off_label"
          label="Label 'uit'"
          v-model="optionForm.toggleConfig.offLabel"
          placeholder="Nee"
        />
      </div>
      <div class="form-toggles">
        <ToggleInput
          id="toggle_default_value"
          label="Standaard aan"
          v-model="optionForm.toggleConfig.defaultValue"
        />
      </div>
      <p class="field-description">
        Deze optie kan ook als dimensie in de prijsmatrix gebruikt worden (twee waarden: aan / uit).
      </p>
    </div>

    <!-- Choice configuratie -->
    <div
      v-if="inputs.type.binding === 'single-choice' || inputs.type.binding === 'multi-choice'"
      class="config-section"
    >
      <h4>Keuze Configuratie</h4>

      <SelectInput
        v-if="inputs.type.binding === 'single-choice'"
        id="display_layout"
        label="Weergave"
        v-model="optionForm.displaySettings.layout"
        :options="[
          { value: 'default', label: 'Standaard (radio buttons)' },
          { value: 'select', label: 'Select (dropdown)' },
        ]"
      />

      <MessageElement
        v-if="
          inputs.type.binding === 'single-choice' &&
          optionForm.displaySettings.layout === 'select' &&
          hasHiddenChoiceData
        "
        class="dropdown-hidden-data-hint"
      >
        In de dropdown-weergave zie je per keuze alleen de naam (+ prijs).
        Afbeeldingen, beschrijvingen en USP's die je bij de keuzes hebt ingesteld, worden hier
        <strong>niet getoond</strong>.
      </MessageElement>

      <div class="display-settings" v-if="optionForm.displaySettings.layout !== 'select'">
        <div class="form-toggles">
          <ToggleInput
            id="display_show_price"
            label="Toon prijs"
            v-model="optionForm.displaySettings.showPrice"
          />
          <ToggleInput
            id="display_show_image"
            label="Toon afbeelding"
            v-model="optionForm.displaySettings.showImage"
          />
          <ToggleInput
            id="display_show_description"
            label="Toon beschrijving"
            v-model="optionForm.displaySettings.showDescription"
          />
          <ToggleInput
            id="display_show_usps"
            label="Toon USPs"
            v-model="optionForm.displaySettings.showUsps"
          />
        </div>
      </div>

      <div v-if="choiceSets.length" class="choice-set-section">
        <SelectInput
          id="option_choice_set"
          label="Waardenset"
          :model-value="optionForm.choice_set_id || ''"
          :options="choiceSetOptions"
          @update:model-value="applyChoiceSet"
        />
        <p class="field-description">
          Een set vult de keuzes en houdt label en volgorde gelijk over alle producten. Prijs,
          leverancierscode en prijssleutel blijven per product als je ze hier invult. Een lege
          prijssleutel volgt de set. Vink uit wat dit product niet voert.
        </p>
        <div v-if="boundSet" class="choice-set-values">
          <label
            v-for="value in boundSet.values"
            :key="value.id"
            class="choice-set-value"
            :class="{ 'choice-set-value--off': value.is_active === false }"
          >
            <input
              type="checkbox"
              :checked="isSetValueShown(value.id)"
              :disabled="value.is_active === false"
              @change="toggleSetValue(value.id, $event.target.checked)"
            />
            <span>{{ value.label }}</span>
            <small v-if="value.code">{{ value.code }}</small>
            <small v-if="value.is_active === false">uitgezet in de set</small>
          </label>
        </div>
      </div>

      <div class="choices-section">
        <EmptyState
          v-if="!optionForm.choiceConfig.choices?.length"
          title="Nog geen keuzes toegevoegd"
          :description="
            isSetBound ? 'Vink hierboven waarden aan.' : 'Voeg keuzes toe voor deze optie'
          "
        >
          <Btn
            v-if="!isSetBound"
            variant="secondary"
            @click="addChoice"
            type="button"
            size="sm"
            icon="plus"
          >
            Eerste keuze toevoegen
          </Btn>
        </EmptyState>

        <div v-else class="choices-list">
          <draggable
            v-model="optionForm.choiceConfig.choices"
            item-key="id"
            handle=".choice-drag-handle"
            class="choices-draggable"
            :animation="150"
            :disabled="isSetBound"
          >
            <template #item="{ element: choice, index: choiceIdx }">
          <div
            :key="choice.id"
            class="choice-item-expanded"
          >
            <div class="choice-header">
              <div
                class="choice-drag-handle"
                title="Sleep om de volgorde te wijzigen"
                aria-label="Sleep om de volgorde te wijzigen"
              >
                <BaseIcon name="grip-dots" size="16" />
              </div>
              <h6>Keuze {{ choiceIdx + 1 }}</h6>
              <Btn
                v-if="!isSetBound"
                variant="plain"
                size="sm"
                @click="removeChoice(choiceIdx)"
                type="button"
              >
                <BaseIcon name="xmark" size="14" />
              </Btn>
            </div>

            <div class="choice-basic-fields">
              <TextInput
                :id="`choice_label_${choiceIdx}`"
                label="Label"
                v-model="choice.label"
                placeholder="bijv. Wit, Antraciet"
                :rules="['required']"
                :disabled="isSetBound"
              />
              <SelectInput
                :id="`choice_pricing_type_${choiceIdx}`"
                label="Prijsberekening"
                v-model="choice.pricingType"
                :options="optionPriceTypeOptions"
              />
              <CurrencyInput
                v-if="choice.pricingType !== 'formula'"
                :id="`choice_price_${choiceIdx}`"
                label="Prijs"
                v-model="choice.price"
                :allow-negative="true"
              />
              <TextInput
                :id="`choice_sku_suffix_${choiceIdx}`"
                label="SKU Suffix"
                v-model="choice.skuSuffix"
                placeholder="WIT"
              />
              <TextInput
                :id="`choice_supplier_code_${choiceIdx}`"
                label="Leverancierscode"
                v-model="choice.supplierCode"
                placeholder="bijv. 17.72"
              />
              <TextInput
                :id="`choice_price_key_${choiceIdx}`"
                label="Prijssleutel"
                v-model="choice.priceKey"
                placeholder="bijv. S"
              />
            </div>

            <FormulaEditor
              v-if="choice.pricingType === 'formula'"
              :id="`choice_price_formula_${choiceIdx}`"
              class="choice-formula-field"
              label="Prijsformule"
              v-model="choice.priceFormula"
              :custom-fields="formulaFields"
              :show-conditional-operators="false"
              compact
            />

            <div class="choice-default-section">
              <CheckboxInput
                v-if="inputs.type.binding === 'single-choice'"
                :id="`choice_default_${choiceIdx}`"
                label="Default keuze"
                :model-value="choice.isDefault || false"
                @update:model-value="(value) => setDefaultChoice(choiceIdx, value)"
              />
              <CheckboxInput
                v-else-if="inputs.type.binding === 'multi-choice'"
                :id="`choice_default_${choiceIdx}`"
                label="Standaard geselecteerd"
                v-model="choice.isDefault"
              />
              <CheckboxInput
                :id="`choice_internal_${choiceIdx}`"
                label="Alleen intern"
                v-model="choice.internalOnly"
                :disabled="choice.externalOnly"
              />
              <CheckboxInput
                :id="`choice_external_${choiceIdx}`"
                label="Alleen extern"
                v-model="choice.externalOnly"
                :disabled="choice.internalOnly"
              />
            </div>

            <!-- Conditionally show extra fields based on display settings -->
            <p v-if="isSetBound" class="field-description">
              Label, beschrijving, afbeelding en USP's komen uit de set. Prijs, leverancierscode,
              SKU-suffix en een ingevulde prijssleutel zijn van dit product. Een lege prijssleutel
              volgt de set.
            </p>

            <div
              v-if="optionForm.displaySettings.showDescription"
              :class="{ 'set-locked': isSetBound }"
            >
              <TextAreaInput
                :id="`choice_description_${choiceIdx}`"
                label="Beschrijving"
                v-model="choice.description"
                placeholder="Beschrijving van deze keuze..."
              />
            </div>

            <div
              v-if="optionForm.displaySettings.showImage"
              class="file-input-wrapper"
              :class="{ 'set-locked': isSetBound }"
            >
              <FileInput
                :id="`choice_image_${choiceIdx}`"
                label="Afbeelding"
                v-model="choice.image"
                :upload-config="{
                  type: 'choice_image',
                  context: 'product_option',
                  choiceId: choice.id || `choice_${choiceIdx}`,
                }"
                accept="image/*"
                @click.stop
              />
            </div>

            <div
              v-if="optionForm.displaySettings.showUsps"
              class="choice-usps-section"
              :class="{ 'set-locked': isSetBound }"
            >
              <USPInput
                :id="`choice_usps_${choiceIdx}`"
                label="USPs voor deze keuze"
                v-model="choice.usps"
                :max="3"
              />
            </div>

            <!-- Choice-level conditions -->
            <div v-if="conditionableOptions.length > 0" class="choice-conditions-section">
              <div
                class="choice-conditions-toggle"
                @click="toggleChoiceConditions(choiceIdx)"
              >
                <BaseIcon
                  :name="choiceConditionsExpanded[choiceIdx] ? 'chevron-down' : 'chevron'"
                  size="12"
                />
                <span>Voorwaarden</span>
                <span v-if="choice.conditions?.rules?.length" class="choice-conditions-badge">
                  {{ choice.conditions.rules.length }}
                </span>
              </div>
              <div v-if="choiceConditionsExpanded[choiceIdx]" class="choice-conditions-body">
                <template v-if="!choice.conditions">
                  <Btn
                    variant="dashed"
                    size="sm"
                    icon="plus"
                    @click="initChoiceConditions(choiceIdx)"
                    type="button"
                  >
                    Voorwaarde toevoegen
                  </Btn>
                </template>
                <template v-else>
                  <CPQConditionGroup
                    :group="choice.conditions"
                    :attributes="choiceConditionAttributes"
                    :canRemove="false"
                  />
                  <Btn
                    variant="plain"
                    size="sm"
                    @click="clearChoiceConditions(choiceIdx)"
                    type="button"
                    class="mt-2"
                  >
                    Voorwaarden verwijderen
                  </Btn>
                </template>
              </div>
            </div>
          </div>
            </template>
          </draggable>
          <Btn v-if="!isSetBound" variant="secondary" size="sm" @click="addChoice" type="button">
            <BaseIcon name="plus" size="14" />
            Keuze Toevoegen
          </Btn>
        </div>
      </div>
    </div>

    <!-- Zichtbaarheidsvoorwaarden -->
    <div class="config-section">
      <h4>Zichtbaarheidsvoorwaarden</h4>
      <p class="field-description">
        Toon of verberg deze optie op basis van de waarde van andere opties.
      </p>

      <div v-if="conditionableOptions.length === 0" class="conditions-empty">
        <small>Voeg eerst andere opties toe om voorwaarden te configureren.</small>
      </div>

      <template v-else>
        <div class="conditions-header">
          <SelectInput
            id="conditions_type"
            label="Actie"
            v-model="optionForm.conditions.type"
            :options="[
              { value: 'show', label: 'Toon deze optie als...' },
              { value: 'hide', label: 'Verberg deze optie als...' },
            ]"
          />
        </div>

        <div v-if="optionForm.conditions.groups.length > 0" class="conditions-groups">
          <div
            v-for="(group, gIdx) in optionForm.conditions.groups"
            :key="gIdx"
            class="condition-group-block"
          >
            <div class="condition-group-block__header">
              <span v-if="gIdx === 0">Wanneer</span>
              <SelectInput
                v-else
                :id="`conditions_between_${gIdx}`"
                v-model="optionForm.conditions.betweenGroupLogic"
                :options="[
                  { value: 'AND', label: 'EN' },
                  { value: 'OR', label: 'OF' },
                ]"
                class="logic-select"
              />
              <Btn
                v-if="optionForm.conditions.groups.length > 1"
                variant="plain"
                size="sm"
                @click="removeConditionGroup(gIdx)"
                type="button"
              >
                <BaseIcon name="xmark" size="14" />
              </Btn>
            </div>

            <div
              v-for="(rule, rIdx) in group.rules"
              :key="rIdx"
              class="condition-rule-row"
            >
              <span v-if="rIdx > 0" class="rule-logic-label">
                {{ group.logic === 'AND' ? 'EN' : 'OF' }}
              </span>
              <SelectInput
                v-if="rIdx === 1"
                :id="`group_logic_${gIdx}`"
                v-model="group.logic"
                :options="[
                  { value: 'AND', label: 'EN (alle)' },
                  { value: 'OR', label: 'OF (één van)' },
                ]"
                class="logic-select"
              />

              <SelectInput
                :id="`rule_option_${gIdx}_${rIdx}`"
                v-model="rule.optionId"
                :options="conditionableOptions"
                placeholder="Kies optie..."
                class="rule-field"
              />
              <SelectInput
                :id="`rule_operator_${gIdx}_${rIdx}`"
                v-model="rule.operator"
                :options="getOperatorOptions(rule.optionId)"
                class="rule-field rule-field--sm"
              />
              <template v-if="getOptionType(rule.optionId) === 'number'">
                <NumberInput
                  :id="`rule_value_${gIdx}_${rIdx}`"
                  v-model="rule.value"
                  placeholder="Waarde"
                  class="rule-field rule-field--sm"
                />
              </template>
              <template v-else>
                <SelectInput
                  :id="`rule_value_${gIdx}_${rIdx}`"
                  v-model="rule.value"
                  :options="getChoiceOptions(rule.optionId)"
                  placeholder="Kies waarde..."
                  class="rule-field"
                />
              </template>
              <Btn
                v-if="group.rules.length > 1"
                variant="plain"
                size="sm"
                @click="removeConditionRule(gIdx, rIdx)"
                type="button"
              >
                <BaseIcon name="xmark" size="14" />
              </Btn>
            </div>

            <Btn
              variant="dashed"
              size="sm"
              @click="addConditionRule(gIdx)"
              type="button"
              icon="plus"
            >
              Regel toevoegen
            </Btn>
          </div>
        </div>

        <div class="conditions-actions">
          <Btn
            variant="dashed"
            size="sm"
            @click="addConditionGroup"
            type="button"
            icon="plus-circle"
          >
            {{ optionForm.conditions.groups.length === 0 ? 'Voorwaarde toevoegen' : 'Groep toevoegen' }}
          </Btn>
        </div>
      </template>
    </div>

    <div class="modal__actions">
      <Btn variant="secondary" @click="handleCancel" type="button">Annuleren</Btn>
      <Btn variant="primary" @click="handleSubmit" type="button">
        {{ isEditing ? 'Wijzigingen opslaan' : 'Optie toevoegen' }}
      </Btn>
    </div>
  </div>
</template>

<script>
  import { ref, watch, computed } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import { useValidation } from '@/composables/useValidation';
  import { uid } from 'uid';
  import { PricingEngine } from '@shared/services/pricingEngine';

  // Input components
  import TextInput from '@/components/inputs/TextInput.vue';
  import NumberInput from '@/components/inputs/NumberInput.vue';
  import TextAreaInput from '@/components/inputs/TextAreaInput.vue';
  import CheckboxInput from '@/components/inputs/CheckboxInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import CurrencyInput from '@/components/inputs/CurrencyInput.vue';
  import FileInput from '@/components/inputs/FileInput.vue';
  import USPInput from '@/components/inputs/USPInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import WysiwygEditor from '@/components/inputs/WysiwygEditor.vue';
  import FormulaEditor from '@/components/inputs/FormulaEditor.vue';

  // UI components
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import MessageElement from '@/components/partials/Message.vue';
  import CPQConditionGroup from '@/pages/flow/CPQConditionGroup.vue';
  import draggable from 'vuedraggable';

  export default {
    name: 'ProductOptionModal',

    components: {
      TextInput,
      NumberInput,
      TextAreaInput,
      CheckboxInput,
      ToggleInput,
      CurrencyInput,
      FileInput,
      USPInput,
      SelectInput,
      WysiwygEditor,
      FormulaEditor,
      Btn,
      BaseIcon,
      EmptyState,
      MessageElement,
      CPQConditionGroup,
      draggable,
    },

    emits: ['submit', 'cancel', 'create-group'],

    props: {
      option: {
        type: Object,
        default: () => null,
      },
      availableOptions: {
        type: Array,
        default: () => [],
        description: 'Other product options that can be used for matrix pricing',
      },
      optionGroups: {
        type: Array,
        default: () => [],
        description: 'Available option groups for page grouping',
      },
      computedFields: {
        type: Array,
        default: () => [],
        description: 'Computed fields that can be used in price formulas',
      },
      choiceSets: {
        type: Array,
        default: () => [],
        description: 'Catalog value sets a choice option can be bound to (catalog masters only)',
      },
      initialGroupId: {
        type: String,
        default: '',
      },
    },

    setup(props, { emit }) {
      const uiStore = useUIStore();

      const isEditing = computed(() => !!props.option);
      const isContentType = computed(
        () =>
          inputs.value.type.binding === 'notice' ||
          inputs.value.type.binding === 'image' ||
          inputs.value.type.binding === 'photo'
      );

      // Preview values for pricing calculation
      const previewValue = ref(50);
      const previewOtherValue = ref(100);
      const optionPriceTypeOptions = [
        { value: 'fixed', label: 'Vaste prijs' },
        { value: 'formula', label: 'Formule' },
      ];

      // Pricing engine instance for this modal
      const pricingEngine = new PricingEngine();

      // Helper function to check if a choice option has numeric values
      const hasNumericChoices = (option) => {
        console.log('option', option);
        if (option.type !== 'single-choice' && option.type !== 'multi-choice') {
          return false;
        }

        const choices = option.choiceConfig?.choices || [];
        if (choices.length === 0) return false;

        // Check if all choice values are numeric (using label)
        return choices.every((choice) => {
          const value = choice.label;
          const parsed = parseFloat(value);
          return !isNaN(parsed) && isFinite(parsed);
        });
      };

      // Matrix field options - get from parent component (other product options)
      const matrixFieldOptions = computed(() => {
        // Filter out current option and show number-type options OR numeric choice options
        const currentOptionId = props.option?.id;
        return props.availableOptions
          .filter((opt) => {
            if (opt.id === currentOptionId) return false;

            // Include number type options
            if (opt.type === 'number') return true;

            // Include single/multi-choice options with numeric values
            return hasNumericChoices(opt);
          })
          .map((opt) => ({
            value: opt.id,
            label: `${opt.label || `Option ${opt.id}`} (${opt.type === 'number' ? 'number' : 'numeric choice'})`,
          }));
      });

      const formulaFields = computed(() => {
        const currentOptionId = props.option?.id;
        const fields = [
          {
            id: 'value',
            label: 'Ingevoerde waarde',
            type: 'number',
          },
        ];

        props.availableOptions
          .filter((option) => option.id !== currentOptionId)
          .filter((option) => ['number', 'single-choice', 'multi-choice', 'toggle'].includes(option.type))
          .forEach((option) => {
            fields.push({
              id: option.id,
              label: option.label || option.id,
              type: option.type === 'number' ? 'number' : 'single-choice',
              choices:
                option.type === 'single-choice' || option.type === 'multi-choice'
                  ? option.choiceConfig?.choices
                  : null,
            });
          });

        props.computedFields.forEach((field) => {
          if (!field?.id || !field?.label) return;
          fields.push({
            id: field.id,
            label: field.label,
            type: 'formula',
          });
        });

        return fields;
      });

      // Validation setup
      const { inputs, validateForm, onValidate } = useValidation({
        label: {
          id: 'option_label',
          label: 'Label',
          binding: '',
          error: '',
          rules: ['required', 'maxLength:100'],
          placeholder: 'bijv. Breedte, Kleur, Bediening',
        },
        caption: {
          id: 'option_caption',
          label: 'Caption',
          binding: '',
          error: '',
          rules: ['maxLength:255'],
          placeholder: 'Optionele ondertitel of uitleg',
        },
        type: {
          id: 'option_type',
          label: 'Type',
          binding: 'number',
          error: '',
          rules: ['required'],
        },
      });

      const PAGE_GROUP_NONE = '';

      const resolvePageGroupId = (id) => {
        if (!id) return PAGE_GROUP_NONE;
        const validIds = (props.optionGroups || []).map((g) => g.id);
        return validIds.includes(id) ? id : PAGE_GROUP_NONE;
      };

      const optionForm = ref({
        required: false,
        showOnQuote: false,
        hideOnQuote: false,
        showOnPackingSlip: true,
        internalOnly: false,
        externalOnly: false,
        pageGroupId: PAGE_GROUP_NONE,
        autoFill: false,
        width: 'full',
        choice_set_id: null,
        choice_set_excluded_value_ids: [],
        displaySettings: {
          layout: 'default',
          showPrice: true,
          showImage: false,
          showDescription: false,
          showUsps: false,
          add_search_bar: false,
        },
        textConfig: {
          variant: 'short-answer',
          placeholder: '',
        },
        noticeConfig: {
          content: '',
          variant: 'info',
          showIcon: true,
        },
        imageConfig: {
          src: '',
          alt: '',
          alignment: 'center',
        },
        photoConfig: {
          min: 1,
          max: 5,
          instruction: '',
        },
        numberConfig: {
          displayStyle: 'input',
          min: 0,
          max: 100,
          step: 1,
          basePrice: 0,
          pricePerUnit: 0,
          defaultValue: null,
          pricingType: 'per_unit',
          fixedPrice: 0,
          formula: '',
          matrixConfig: {
            otherField: '',
            pricePerUnit: 0,
            minimumPrice: 0,
          },
        },
        toggleConfig: {
          pricingType: 'fixed',
          price: 0,
          priceFormula: '',
          defaultValue: false,
          onLabel: 'Ja',
          offLabel: 'Nee',
          skuSuffix: '',
        },
        choiceConfig: {
          choices: [],
        },
        conditions: {
          type: 'show',
          betweenGroupLogic: 'AND',
          groups: [],
        },
      });

      if (!props.option && props.initialGroupId) {
        optionForm.value.pageGroupId = resolvePageGroupId(props.initialGroupId);
      }

      // A dropdown option renders only the choice name (+ price) in the funnel;
      // per-choice images/descriptions/USPs are dropped. Surface a heads-up when
      // the option actually has such data that would silently disappear on switch.
      const hasHiddenChoiceData = computed(() =>
        (optionForm.value.choiceConfig?.choices || []).some(
          (c) => c.image || c.description || (c.usps && c.usps.length)
        )
      );

      const pageGroupOptions = computed(() => {
        return [
          { value: PAGE_GROUP_NONE, label: 'Geen groep (eigen pagina)' },
          ...(props.optionGroups || []).map((group) => ({
            value: group.id,
            label: group.name,
          })),
        ];
      });

      // Handle creating a new group
      const handleCreateGroup = (newOption) => {
        // When creating a new group, we need to emit an event to create it
        // For now, we'll just set it to the label and let the parent handle creation
        // The parent should create the group and pass it back
        emit('create-group', {
          name: newOption.value || newOption.label,
        });
        // Note: The parent should update optionGroups and re-open modal or update the value
      };

      // Watch for option changes (when editing)
      watch(
        () => props.option,
        (newOption) => {
          if (newOption) {
            // Load existing option data into inputs
            inputs.value.label.binding = newOption.label || '';
            inputs.value.caption.binding = newOption.caption || '';
            inputs.value.type.binding = newOption.type || 'number';

            // Load other option data into optionForm
            Object.assign(optionForm.value, {
              required: newOption.required || false,
              showOnQuote: newOption.showOnQuote || false,
              hideOnQuote: newOption.hideOnQuote || false,
              showOnPackingSlip: newOption.showOnPackingSlip || false,
              internalOnly: newOption.internalOnly || false,
              externalOnly: newOption.externalOnly || false,
              pageGroupId: resolvePageGroupId(newOption.pageGroupId),
              autoFill: newOption.autoFill || false,
              width: newOption.width || 'full',
              choice_set_id: newOption.choice_set_id || null,
              choice_set_excluded_value_ids: newOption.choice_set_excluded_value_ids || [],
              displaySettings: {
                layout: 'default',
                showPrice: true,
                showImage: false,
                showDescription: false,
                showUsps: false,
                add_search_bar: false,
                ...newOption.displaySettings,
              },
              textConfig: {
                variant: 'short-answer',
                placeholder: '',
                ...newOption.textConfig,
              },
              noticeConfig: {
                content: '',
                variant: 'info',
                showIcon: true,
                ...newOption.noticeConfig,
              },
              imageConfig: {
                src: '',
                alt: '',
                alignment: 'center',
                ...newOption.imageConfig,
              },
              photoConfig: {
                min: 1,
                max: 5,
                instruction: '',
                ...newOption.photoConfig,
              },
              numberConfig: {
                displayStyle: 'input',
                min: 0,
                max: 100,
                step: 1,
                basePrice: 0,
                pricePerUnit: 0,
                defaultValue: null,
                pricingType: 'per_unit',
                fixedPrice: 0,
                formula: '',
                matrixConfig: {
                  otherField: '',
                  pricePerUnit: 0,
                  minimumPrice: 0,
                },
                ...newOption.numberConfig,
              },
              toggleConfig: {
                pricingType: 'fixed',
                price: 0,
                priceFormula: '',
                defaultValue: false,
                onLabel: 'Ja',
                offLabel: 'Nee',
                skuSuffix: '',
                ...newOption.toggleConfig,
              },
              choiceConfig: {
                ...newOption.choiceConfig,
                // Fix existing choices that have empty IDs
                choices: (newOption.choiceConfig?.choices || []).map((choice, index) => ({
                  pricingType: 'fixed',
                  priceFormula: '',
                  ...choice,
                  id: choice.id || `choice_${uid()}`,
                })),
              },
              conditions: {
                type: 'show',
                betweenGroupLogic: 'AND',
                groups: [],
                ...newOption.conditions,
              },
            });
          } else {
            // Reset to defaults for new option
            inputs.value.label.binding = '';
            inputs.value.caption.binding = '';
            inputs.value.type.binding = 'number';

            Object.assign(optionForm.value, {
              required: false,
              showOnQuote: false,
              hideOnQuote: false,
              showOnPackingSlip: true,
              internalOnly: false,
              externalOnly: false,
              pageGroupId: null,
              autoFill: false,
              width: 'full',
              choice_set_id: null,
              choice_set_excluded_value_ids: [],
              displaySettings: {
                layout: 'default',
                showPrice: true,
                showImage: false,
                showDescription: false,
                showUsps: false,
                add_search_bar: false,
              },
              textConfig: {
                variant: 'short-answer',
                placeholder: '',
              },
              noticeConfig: {
                content: '',
                variant: 'info',
                showIcon: true,
              },
              imageConfig: {
                src: '',
                alt: '',
                alignment: 'center',
              },
              photoConfig: {
                min: 1,
                max: 5,
                instruction: '',
              },
              numberConfig: {
                displayStyle: 'input',
                min: 0,
                max: 100,
                step: 1,
                basePrice: 0,
                pricePerUnit: 0,
                defaultValue: null,
                pricingType: 'per_unit',
                fixedPrice: 0,
                formula: '',
                matrixConfig: {
                  otherField: '',
                  pricePerUnit: 0,
                  minimumPrice: 0,
                },
              },
              toggleConfig: {
                pricingType: 'fixed',
                price: 0,
                priceFormula: '',
                defaultValue: false,
                onLabel: 'Ja',
                offLabel: 'Nee',
                skuSuffix: '',
              },
              choiceConfig: {
                choices: [],
              },
              conditions: {
                type: 'show',
                betweenGroupLogic: 'AND',
                groups: [],
              },
            });
          }
        },
        { immediate: true }
      );

      // ---- Value sets (catalog masters) ------------------------------------
      const isSetBound = computed(() => Boolean(optionForm.value.choice_set_id));
      const boundSet = computed(
        () => props.choiceSets.find((set) => set.id === optionForm.value.choice_set_id) || null
      );
      const choiceSetOptions = computed(() => [
        { value: '', label: 'Geen set, eigen keuzes' },
        ...props.choiceSets.map((set) => ({
          value: set.id,
          label: set.catalog_supplier_id ? set.name : `${set.name} (globaal)`,
        })),
      ]);

      // Same shape as the API expansion: id from the set value, label and order
      // from the set, price and supplier code kept from the product.
      const rebuildChoicesFromSet = () => {
        const set = boundSet.value;
        if (!set) return;
        const excluded = new Set(optionForm.value.choice_set_excluded_value_ids || []);
        const existing = new Map(
          (optionForm.value.choiceConfig.choices || []).map((choice) => [
            choice.choice_set_value_id || choice.id,
            choice,
          ])
        );
        optionForm.value.choiceConfig.choices = (set.values || [])
          .filter((value) => value.is_active !== false && !excluded.has(value.id))
          .map((value) => {
            const id = `choice_set_${value.id}`;
            const previous = existing.get(value.id) || existing.get(id) || {};
            return {
              pricingType: 'fixed',
              price: 0,
              priceFormula: '',
              isDefault: false,
              conditions: null,
              ...previous,
              id,
              choice_set_value_id: value.id,
              label: value.label,
              image: value.image_url || '',
              description: value.description || '',
              usps: Array.isArray(value.usps) ? value.usps : [],
              supplierCode: previous.supplierCode || value.code || '',
              skuSuffix: previous.skuSuffix || value.sku_suffix || '',
              priceKey:
                (typeof previous.priceKey === 'string' && previous.priceKey.trim()) ||
                (typeof value.price_key === 'string' && value.price_key.trim()) ||
                '',
              group: value.group_label || null,
              internalOnly: value.internal_only === true || previous.internalOnly === true,
              externalOnly: value.external_only === true || previous.externalOnly === true,
            };
          });
      };

      const applyChoiceSet = (setId) => {
        optionForm.value.choice_set_id = setId || null;
        optionForm.value.choice_set_excluded_value_ids = [];
        if (setId) rebuildChoicesFromSet();
      };

      const isSetValueShown = (valueId) =>
        !(optionForm.value.choice_set_excluded_value_ids || []).includes(valueId);

      const toggleSetValue = (valueId, shown) => {
        const list = new Set(optionForm.value.choice_set_excluded_value_ids || []);
        if (shown) list.delete(valueId);
        else list.add(valueId);
        optionForm.value.choice_set_excluded_value_ids = [...list];
        rebuildChoicesFromSet();
      };

      const addChoice = () => {
        // Generate unique ID for new choice using uid package
        const choiceId = `choice_${uid()}`;

        optionForm.value.choiceConfig.choices.push({
          id: choiceId,
          label: '',
          pricingType: 'fixed',
          price: 0,
          priceFormula: '',
          skuSuffix: '',
          supplierCode: '',
          priceKey: '',
          description: '',
          image: '',
          usps: [],
          isDefault: false,
          internalOnly: false,
          externalOnly: false,
          conditions: null,
        });
      };

      const removeChoice = (idx) => {
        optionForm.value.choiceConfig.choices.splice(idx, 1);
      };

      const setDefaultChoice = (choiceIdx, isDefault) => {
        if (isDefault && inputs.value.type.binding === 'single-choice') {
          // Voor single-choice: zet alle andere choices op false
          optionForm.value.choiceConfig.choices.forEach((choice, idx) => {
            choice.isDefault = idx === choiceIdx;
          });
        } else {
          // Voor multi-choice of als isDefault false is
          optionForm.value.choiceConfig.choices[choiceIdx].isDefault = isDefault;
        }
      };

      // Choice-level conditions
      const choiceConditionsExpanded = ref({});

      const choiceConditionAttributes = computed(() => {
        const currentId = props.option?.id;
        const attributes = [];
        props.availableOptions
          .filter((o) => o.id !== currentId)
          .filter((o) => ['number', 'single-choice', 'multi-choice', 'toggle'].includes(o.type))
          .forEach((option) => {
            const isChoice = option.type === 'single-choice' || option.type === 'multi-choice';
            const choices = option.choiceConfig?.choices || [];
            if (option.type === 'toggle') {
              attributes.push({
                key: option.id,
                label: option.label,
                type: 'select',
                options: [
                  { value: 'true', label: option.toggleConfig?.onLabel || 'Ja' },
                  { value: 'false', label: option.toggleConfig?.offLabel || 'Nee' },
                ],
              });
            } else if (isChoice && choices.length > 0) {
              attributes.push({
                key: option.id,
                label: option.label,
                type: 'select',
                options: choices.map((c) => ({
                  value: c.id,
                  label: c.label || c.id,
                })),
              });
            } else {
              attributes.push({
                key: option.id,
                label: option.label,
                type: option.type === 'number' ? 'number' : 'text',
              });
            }
          });
        return attributes;
      });

      const toggleChoiceConditions = (choiceIdx) => {
        choiceConditionsExpanded.value[choiceIdx] = !choiceConditionsExpanded.value[choiceIdx];
      };

      const initChoiceConditions = (choiceIdx) => {
        optionForm.value.choiceConfig.choices[choiceIdx].conditions = {
          logic: 'AND',
          rules: [{ attribute: null, operator: '==', value: '' }],
          subGroups: [],
        };
      };

      const clearChoiceConditions = (choiceIdx) => {
        optionForm.value.choiceConfig.choices[choiceIdx].conditions = null;
      };

      // Update pricing engine when config changes
      watch(
        () => optionForm.value.numberConfig,
        (newConfig) => {
          pricingEngine.registerOption('preview', newConfig);
        },
        { deep: true, immediate: true }
      );

      // Helper to resolve choice ID to numeric value for preview
      const resolveChoiceValue = (optionId, choiceValue) => {
        const option = props.availableOptions.find((opt) => opt.id === optionId);
        if (!option || option.type === 'number') {
          return choiceValue; // Direct numeric value
        }

        // For choice options, find the choice and parse its label
        const choice = option.choiceConfig?.choices?.find((c) => c.id === choiceValue);
        if (choice) {
          const parsed = parseFloat(choice.label);
          return isNaN(parsed) ? 0 : parsed;
        }

        return 0;
      };

      // Computed properties for preview using pricing engine
      const calculatedPreviewPrice = computed(() => {
        const otherValues = {};
        if (optionForm.value.numberConfig.pricingType === 'matrix') {
          const otherFieldId = optionForm.value.numberConfig.matrixConfig?.otherField;
          if (otherFieldId) {
            // Resolve the other field's value (could be choice ID or direct number)
            otherValues[otherFieldId] = resolveChoiceValue(otherFieldId, previewOtherValue.value);
          }
        }

        // Resolve current preview value
        const currentValue = resolveChoiceValue('preview', previewValue.value);

        const price = pricingEngine.calculateOptionPrice('preview', currentValue, otherValues);
        return price.toFixed(2);
      });

      const previewCalculation = computed(() => {
        const config = optionForm.value.numberConfig;
        const otherValues = {};
        if (config.pricingType === 'matrix') {
          const otherFieldId = config.matrixConfig?.otherField;
          if (otherFieldId) {
            otherValues[otherFieldId] = resolveChoiceValue(otherFieldId, previewOtherValue.value);
          }
        }

        const currentValue = resolveChoiceValue('preview', previewValue.value);
        return pricingEngine.getCalculationDescription(config, currentValue, otherValues);
      });

      // Conditions editor helpers
      const conditionableOptions = computed(() => {
        const currentId = props.option?.id;
        return props.availableOptions
          .filter((o) => o.id !== currentId)
          .filter((o) => ['number', 'single-choice', 'multi-choice', 'toggle'].includes(o.type))
          .map((o) => ({
            value: o.id,
            label: o.label || o.id,
          }));
      });

      const getOptionType = (optionId) => {
        const opt = props.availableOptions.find((o) => o.id === optionId);
        return opt?.type || 'text';
      };

      const getOperatorOptions = (optionId) => {
        const type = getOptionType(optionId);
        if (type === 'number') {
          return [
            { value: 'equals', label: 'is gelijk aan' },
            { value: 'not_equals', label: 'is niet gelijk aan' },
            { value: 'greater_than', label: 'is groter dan' },
            { value: 'less_than', label: 'is kleiner dan' },
          ];
        }
        return [
          { value: 'equals', label: 'is gelijk aan' },
          { value: 'not_equals', label: 'is niet gelijk aan' },
        ];
      };

      const getChoiceOptions = (optionId) => {
        const opt = props.availableOptions.find((o) => o.id === optionId);
        if (opt?.type === 'toggle') {
          return [
            { value: 'true', label: opt.toggleConfig?.onLabel || 'Ja' },
            { value: 'false', label: opt.toggleConfig?.offLabel || 'Nee' },
          ];
        }
        if (!opt?.choiceConfig?.choices) return [];
        return opt.choiceConfig.choices.map((c) => ({
          value: c.id,
          label: c.label || c.id,
        }));
      };

      // Dynamische grenzen (min/max gekoppeld aan een andere optie, bv. uitval)
      const dynamicBoundKinds = [
        { key: 'dynamicMin', label: 'minimum' },
        { key: 'dynamicMax', label: 'maximum' },
      ];

      const toggleDynamicBound = (kind, enabled) => {
        optionForm.value.numberConfig[kind] = enabled
          ? { optionId: '', map: [], fallback: null }
          : null;
      };

      const onDynamicBoundOptionChange = (kind) => {
        if (optionForm.value.numberConfig[kind]) {
          optionForm.value.numberConfig[kind].map = [];
        }
      };

      const dynamicBoundRefType = (kind) =>
        getOptionType(optionForm.value.numberConfig[kind]?.optionId);

      const addDynamicBoundRow = (kind) => {
        optionForm.value.numberConfig[kind].map.push({ value: '', bound: null });
      };

      const removeDynamicBoundRow = (kind, idx) => {
        optionForm.value.numberConfig[kind].map.splice(idx, 1);
      };

      const fillDynamicBoundChoices = (kind) => {
        const config = optionForm.value.numberConfig[kind];
        if (!config) return;
        const existing = config.map;
        getChoiceOptions(config.optionId).forEach((choice) => {
          if (!existing.some((row) => String(row.value) === String(choice.value))) {
            existing.push({ value: choice.value, bound: null });
          }
        });
      };

      const addConditionGroup = () => {
        optionForm.value.conditions.groups.push({
          logic: 'AND',
          rules: [{ optionId: '', operator: 'equals', value: '' }],
        });
      };

      const removeConditionGroup = (gIdx) => {
        optionForm.value.conditions.groups.splice(gIdx, 1);
      };

      const addConditionRule = (gIdx) => {
        optionForm.value.conditions.groups[gIdx].rules.push({
          optionId: '',
          operator: 'equals',
          value: '',
        });
      };

      const removeConditionRule = (gIdx, rIdx) => {
        optionForm.value.conditions.groups[gIdx].rules.splice(rIdx, 1);
      };

      const handleSubmit = async () => {
        // Validate form first
        const isValid = await validateForm();
        if (!isValid) {
          return;
        }

        // Combine inputs with optionForm data
        const optionData = {
          id: props.option?.id || `option_${uid()}`,
          label: inputs.value.label.binding,
          caption: inputs.value.caption.binding,
          type: inputs.value.type.binding,
          ...JSON.parse(JSON.stringify(optionForm.value)),
        };

        optionData.pageGroupId = optionData.pageGroupId || null;

        // Clean empty condition groups
        if (optionData.conditions?.groups) {
          optionData.conditions.groups = optionData.conditions.groups.filter(
            (g) => g.rules.length > 0 && g.rules.some((r) => r.optionId)
          );
        }

        emit('submit', optionData);
      };

      const handleCancel = () => {
        emit('cancel');
      };

      return {
        // Validation
        inputs,
        onValidate,
        // Form data
        optionForm,
        isEditing,
        isContentType,
        hasHiddenChoiceData,
        // Preview
        previewValue,
        previewOtherValue,
        matrixFieldOptions,
        formulaFields,
        optionPriceTypeOptions,
        calculatedPreviewPrice,
        previewCalculation,
        // Page groups
        pageGroupOptions,
        handleCreateGroup,
        // Dynamic bounds (min/max gekoppeld aan andere optie)
        dynamicBoundKinds,
        toggleDynamicBound,
        onDynamicBoundOptionChange,
        dynamicBoundRefType,
        addDynamicBoundRow,
        removeDynamicBoundRow,
        fillDynamicBoundChoices,
        // Conditions (option-level)
        conditionableOptions,
        getOptionType,
        getOperatorOptions,
        getChoiceOptions,
        addConditionGroup,
        removeConditionGroup,
        addConditionRule,
        removeConditionRule,
        // Conditions (choice-level)
        choiceConditionsExpanded,
        choiceConditionAttributes,
        toggleChoiceConditions,
        initChoiceConditions,
        clearChoiceConditions,
        // Methods
        addChoice,
        removeChoice,
        setDefaultChoice,
        // Value sets
        isSetBound,
        boundSet,
        choiceSetOptions,
        applyChoiceSet,
        isSetValueShown,
        toggleSetValue,
        handleSubmit,
        handleCancel,
      };
    },
  };
</script>

<style scoped lang="scss">
  .form-toggles {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }


  .field-description {
    margin: 0.25rem 0 1rem 0;
    font-size: 0.75rem;
    color: var(--slate-500);
    line-height: 1.4;
  }

  .config-section {
    padding: 0.75rem;
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    background: var(--slate-50);
    margin: 1rem 0;
  }

  .dynamic-bounds {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin: 1rem 0;
  }

  .dynamic-bound {
    &__config {
      margin-top: 0.5rem;
      padding: 0.75rem;
      border: 1px dashed var(--slate-300);
      border-radius: 8px;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__row {
      display: flex;
      align-items: flex-end;
      gap: 0.5rem;

      > *:first-child {
        flex: 1;
      }
    }

    &__actions {
      display: flex;
      gap: 0.5rem;
    }
  }

  .display-settings {
    margin-bottom: 1.5rem;
  }

  .choice-set-section {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    background: var(--slate-50);
  }

  .set-locked {
    pointer-events: none;
    opacity: 0.65;
  }

  .choice-set-values {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0.35rem 1rem;
    margin-top: 0.5rem;
  }

  .choice-set-value {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    @include fontSize('sm');

    small {
      color: var(--slate-500);
    }

    &--off {
      color: var(--slate-400);
    }
  }

  .choices-section {
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      h5 {
        margin: 0;
      }
    }
  }

  .choices-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .choice-item-expanded {
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    padding: 0.5rem;
    background: white;
  }

  .choice-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;

    h6 {
      flex: 1;
      margin: 0;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-700);
    }
  }

  .choices-draggable {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .choice-drag-handle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--slate-400);
    cursor: grab;
    padding: 0.125rem;
    border-radius: 4px;
    user-select: none;
    transition: color 0.15s ease, background-color 0.15s ease;

    &:hover {
      color: var(--slate-700);
      background: var(--slate-100);
    }

    &:active {
      cursor: grabbing;
    }
  }

  .sortable-ghost {
    opacity: 0.5;
    background: var(--brand-50);
  }

  .sortable-chosen {
    background: white;
  }

  .choice-basic-fields {
    display: grid;
    grid-template-columns: minmax(220px, 2fr) 150px 110px 90px 120px 90px;
    gap: 0.75rem;

    @include lg-down {
      grid-template-columns: 1fr 1fr;

      > *:first-child {
        grid-column: 1 / -1;
      }
    }
  }

  .choice-formula-field {
    margin-top: 0.75rem;
    max-width: 560px;
  }

  .choice-default-section {
    display: inline-block;
  }

  .choice-usps-section {
    margin-top: 1rem;
  }

  .choice-conditions-section {
    margin-top: 0.75rem;
    border-top: 1px solid var(--slate-100);
    padding-top: 0.5rem;
  }

  .choice-conditions-toggle {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    cursor: pointer;
    font-size: 0.75rem;
    color: var(--slate-500);
    user-select: none;

    @include hover {
      color: var(--slate-700);
    }
  }

  .choice-conditions-badge {
    background: var(--brand-100);
    color: var(--brand-700);
    font-size: 0.625rem;
    font-weight: 600;
    padding: 0.0625rem 0.375rem;
    border-radius: 10px;
  }

  .choice-conditions-body {
    margin-top: 0.5rem;
    padding: 0.5rem;
    background: var(--slate-50);
    border-radius: 6px;
    border: 1px solid var(--slate-200);
  }

  .mt-2 {
    margin-top: 0.5rem;
  }

  .pricing-section {
    margin-top: 1rem;
    padding: 1rem;
    background: var(--blue-50);
    border-radius: 8px;
    border: 1px solid var(--blue-200);

    h5 {
      margin: 0 0 1rem 0;
      color: var(--blue-900);
      font-weight: 600;
    }

    .pricing-config {
      margin-top: 1rem;
      padding: 0.75rem;
      background: white;
      border-radius: 6px;
      border: 1px solid var(--blue-100);
    }

    .matrix-info {
      margin-bottom: 1rem;
      padding: 0.5rem;
      background: var(--amber-50);
      border-radius: 4px;
      border: 1px solid var(--amber-200);

      p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--amber-800);
      }
    }

    .formula-help {
      margin-top: 0.75rem;
      padding: 0.75rem;
      background: var(--slate-50);
      border-radius: 4px;
      border: 1px solid var(--slate-200);

      small {
        font-size: 0.75rem;
        color: var(--slate-600);
        line-height: 1.4;
      }

      code {
        background: var(--slate-100);
        padding: 0.125rem 0.25rem;
        border-radius: 2px;
        font-family: 'Monaco', 'Consolas', monospace;
        font-size: 0.75rem;
      }
    }

    .pricing-preview {
      margin-top: 1rem;
      padding: 1rem;
      background: var(--green-50);
      border-radius: 6px;
      border: 1px solid var(--green-200);

      h6 {
        margin: 0 0 0.75rem 0;
        color: var(--green-900);
        font-weight: 600;
      }

      .preview-inputs {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 1rem;
        margin-bottom: 1rem;
      }

      .preview-result {
        padding: 0.75rem;
        background: white;
        border-radius: 4px;
        border: 1px solid var(--green-300);

        strong {
          color: var(--green-800);
          font-size: 1.125rem;
        }

        .calculation-breakdown {
          margin-top: 0.5rem;
          color: var(--green-600);
          font-family: 'Monaco', 'Consolas', monospace;
        }
      }
    }
  }

  .conditions-empty {
    padding: 0.75rem;
    color: var(--slate-500);
    font-size: 0.8125rem;
    background: var(--slate-50);
    border-radius: 6px;
    text-align: center;
  }

  .conditions-header {
    margin-bottom: 0.75rem;
    max-width: 300px;
  }

  .conditions-groups {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .condition-group-block {
    padding: 0.75rem;
    border: 1px solid var(--slate-200);
    border-radius: 8px;
    background: white;

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }
  }

  .condition-rule-row {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
  }

  .rule-logic-label {
    font-size: 0.75rem;
    color: var(--slate-500);
    font-weight: 500;
    min-width: 20px;
    text-align: center;
    padding-bottom: 0.5rem;
  }

  .rule-field {
    flex: 1;
    min-width: 120px;

    &--sm {
      max-width: 160px;
    }
  }

  .logic-select {
    max-width: 120px;
  }

  .conditions-actions {
    margin-top: 0.25rem;
  }

  .dropdown-hidden-data-hint {
    margin: 0.5rem 0 1rem;
  }

  .modal__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px solid var(--slate-200);
  }
</style>
